// utils/export-menu.ts — Menu déroulant de choix de format d'export (XLSX/JSON).
//
// Glu DOM partagée, extraite à l'identique de page-actions/render-page et
// page-action-plans (factorisation juin 2026) : les deux portaient le MÊME menu
// flottant (position fixe sous le bouton, fermeture au clic extérieur, nettoyage
// via registerPageDisposer) ; seul le callback de sélection différait.
//
// `esc`, `t` et `registerPageDisposer` sont des globals ambiants (core-globals.d.ts).

export interface ExportMenuOption {
  fmt: string;
  label: string;
}

/**
 * Ouvre, ancré sous `btnEl`, un petit menu de formats d'export et appelle
 * `onSelect(fmt)` au choix de l'utilisateur. Sans `options`, propose XLSX + JSON
 * (clés i18n `pageActions.btn.export*`). Auto-nettoyé au changement de page.
 */
export function openExportMenu(
  btnEl: HTMLElement,
  onSelect: (fmt: string) => void,
  options?: ExportMenuOption[],
): void {
  const opts = options ?? [
    { fmt: 'xlsx', label: t('pageActions.btn.exportXlsx') },
    { fmt: 'json', label: t('pageActions.btn.exportJson') },
  ];
  document.querySelectorAll('.actions-export-menu').forEach((m) => m.remove());
  const rect = btnEl.getBoundingClientRect();
  const menu = document.createElement('div');
  menu.className = 'actions-export-menu';
  menu.style.cssText =
    `position:fixed;top:${rect.bottom + 4}px;left:${rect.left}px;` +
    `background:var(--bg);border:1px solid var(--border2);border-radius:var(--radius);` +
    `box-shadow:0 4px 12px rgba(0,0,0,0.12);padding:4px;z-index:1000;min-width:140px`;
  menu.innerHTML = opts
    .map(
      (o) =>
        `<div class="actions-export-opt menu-hover-bg2" data-export-fmt="${esc(o.fmt)}"
        style="padding:6px 10px;cursor:pointer;font-size:12px;border-radius:4px"
        >${esc(o.label)}</div>`,
    )
    .join('');
  document.body.appendChild(menu);
  menu.addEventListener('click', (e: Event) => {
    const opt = (e.target as HTMLElement | null)?.closest<HTMLElement>('.actions-export-opt');
    if (!opt) return;
    const fmt = opt.dataset['exportFmt'] || 'xlsx';
    menu.remove();
    onSelect(fmt);
  });
  setTimeout(() => {
    const closeOnOutside = (ev: Event): void => {
      const tg = ev.target as Node | null;
      if (tg && !menu.contains(tg)) {
        menu.remove();
        document.removeEventListener('click', closeOnOutside, true);
      }
    };
    document.addEventListener('click', closeOnOutside, true);
    registerPageDisposer(() => {
      document.removeEventListener('click', closeOnOutside, true);
      try {
        menu.remove();
      } catch {
        /* déjà détaché */
      }
    });
  }, 0);
}
